import type {
  IEmployeeBenefit,
  IModularBenefitComponent,
  IModularBenefitContact,
} from '../../../types/modular-benefit';
import { isBoolean } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

export interface ContactOption {
  label: string;
  description?: string;
  key: string;
  subComponents?: IModularBenefitComponent[];
  type?: string;
  disabled?: boolean;
}

export interface ContactSelection {
  id: number;
  values: Record<string, unknown>;
}

interface UseContactSelectionProps {
  value: ContactSelection[];
  contacts: IModularBenefitContact[];
  configWindow?: {
    isOpen?: boolean;
    startTimestamp?: string;
    endTimestamp?: string;
  };
  benefit?: IEmployeeBenefit;
  onChange: (value: ContactSelection[]) => void;
  hasActiveWindowExclusion?: boolean;
}

export const useContactSelection = ({
  value,
  contacts,
  configWindow,
  benefit,
  onChange,
  hasActiveWindowExclusion,
}: UseContactSelectionProps) => {
  const [selectInstanceId, setSelectInstanceId] = useState(0);

  const isConfigWindowOpen =
    (configWindow?.isOpen || benefit?.enrolment?.configWindowOpen) ?? true;

  const isConfigWindowClosed =
    ((!isConfigWindowOpen || benefit?.enrolment?.lockedWindowOpen) &&
      !hasActiveWindowExclusion) ??
    false;

  const requiredContactFields = useMemo(() => {
    const requiredFields: string[] = [];
    if (benefit?.benefit?.benefitRequirements?.includes('DEPENDANT_SSN')) {
      requiredFields.push('ssn');
    }
    if (
      benefit?.benefit?.benefitRequirements?.includes('DEPENDANT_NATIONALITY')
    ) {
      requiredFields.push('nationality');
    }
    return requiredFields;
  }, [benefit]);

  const handleOnChange = useCallback(
    (
      updateSelectedContactIds: string[],
      updateContactSubComponentValues: Record<string, unknown>[]
    ) => {
      const payload: ContactSelection[] =
        updateSelectedContactIds?.map((id) => {
          const foundChildValue = updateContactSubComponentValues?.find(
            (childValue) => childValue.hasOwnProperty(id)
          );
          return {
            id: Number(id),
            values:
              foundChildValue && foundChildValue[id]
                ? (foundChildValue[id] as Record<string, unknown>)
                : {},
          };
        }) || [];

      onChange(payload);
    },
    [onChange]
  );

  const selectedContactIds = useMemo(() => {
    return (
      value
        ?.map((preSelectedValue: ContactSelection) =>
          String(preSelectedValue.id)
        )
        ?.filter((id: string) => {
          const contact = contacts?.find(
            (contact: IModularBenefitContact) => String(contact.id) === id
          );
          return !contact || !contact.disabled;
        }) || []
    );
  }, [value, contacts]);

  const selectedContactDetails = useMemo(
    () =>
      contacts
        ?.filter((contact: IModularBenefitContact) =>
          selectedContactIds.includes(String(contact.id))
        )
        .map((contact: IModularBenefitContact) => {
          const contactTypeKey = contact?.contactTypeKey;
          return {
            label: `${contact?.firstName} ${contact?.lastName}`,
            key: String(contact?.id),
            description: contact?.contactType,
            subComponents: contact?.subComponents,
            type: contactTypeKey,
          };
        }),
    [contacts, selectedContactIds]
  );

  const contactSubComponentValues = useMemo(() => {
    return (
      value?.map((preSelectedValue: ContactSelection) => {
        return {
          [String(preSelectedValue.id)]: preSelectedValue.values,
        };
      }) || []
    );
  }, [value]);

  const handleContactSelectChange = useCallback(
    (changedId?: string) => {
      if (!changedId) return;
      const newSelectedContactIds = [...selectedContactIds, changedId];
      handleOnChange(newSelectedContactIds, contactSubComponentValues);

      setSelectInstanceId(
        (previousSelectInstanceId) => previousSelectInstanceId + 1
      );
    },
    [contactSubComponentValues, handleOnChange, selectedContactIds]
  );

  const handleContactRemove = useCallback(
    (removedId: string) => {
      const newSelectedContactIds = selectedContactIds.filter(
        (id) => id !== removedId
      );
      handleOnChange(newSelectedContactIds, contactSubComponentValues);
    },
    [contactSubComponentValues, handleOnChange, selectedContactIds]
  );

  const handleContactSubComponentValueChange = useCallback(
    (parentKey: string, key: string, value: unknown) => {
      if ((!isBoolean(value) && !value && value !== '') || !key || !parentKey)
        return;
      const index = contactSubComponentValues?.findIndex((item) =>
        item.hasOwnProperty(parentKey)
      );
      const newContactSubComponentValues =
        index === -1
          ? [
              ...contactSubComponentValues,
              {
                [parentKey]: {
                  [key]: value,
                },
              },
            ]
          : [
              ...contactSubComponentValues.slice(0, index),
              {
                ...contactSubComponentValues[index],
                [parentKey]: Object.assign(
                  {},
                  contactSubComponentValues[index][parentKey],
                  {
                    [key]: value,
                  }
                ),
              },
              ...contactSubComponentValues.slice(index + 1),
            ];

      handleOnChange(selectedContactIds, newContactSubComponentValues);
    },
    [contactSubComponentValues, handleOnChange, selectedContactIds]
  );

  return {
    selectInstanceId,
    selectedContactIds,
    isConfigWindowClosed,
    isConfigWindowOpen,
    selectedContactDetails,
    contactSubComponentValues,
    handleContactSelectChange,
    handleContactRemove,
    handleContactSubComponentValueChange,
    requiredContactFields,
  };
};
